// All site copy in one place so the pages stay layout-only. Every number,
// quote, plan and person here is SAMPLE content for the brand site.

export const SERVICES = [
  {
    slug: 'websites',
    icon: 'globe',
    title: 'Contractor websites',
    short: 'Fast, mobile-first sites built to turn visitors into calls and quote requests.',
    body: 'Most contractor sites are brochures. Yours will be a lead machine: every page answers the homeowner\'s question, shows proof, and makes the next step one tap away.',
    includes: [
      'Custom design for your trade and service area',
      'Click-to-call, quote forms and financing CTAs above the fold',
      'Service and city pages written to rank',
      'Hosting, SSL, backups and monthly updates included',
    ],
  },
  {
    slug: 'local-seo',
    icon: 'mapPin',
    title: 'Local SEO and Google Business Profile',
    short: 'Show up in the map pack when homeowners search for your trade nearby.',
    body: 'The map pack is where the phone calls come from. We optimize your Google Business Profile, clean up your listings and build the pages Google needs to rank you in every city you serve.',
    includes: [
      'Google Business Profile optimization and weekly posts',
      'Citation cleanup across 50+ directories',
      'Service-area and city landing pages',
      'Monthly ranking, call and direction-request reports',
    ],
  },
  {
    slug: 'ads',
    icon: 'target',
    title: 'Google Ads and Local Services Ads',
    short: 'Pay-per-lead campaigns tuned every week so your budget goes to booked jobs, not clicks.',
    body: 'We run Local Services Ads and search campaigns by service and city, track every call, and cut what does not convert. You see cost per booked job, not impressions.',
    includes: [
      'Local Services Ads setup and the Google Guaranteed badge',
      'Search campaigns by service and city',
      'Call tracking, recording and lead scoring',
      'Weekly bid, budget and negative-keyword tuning',
    ],
  },
  {
    slug: 'reviews',
    icon: 'star',
    title: 'Reviews and reputation',
    short: 'Automated review requests after every job, and replies handled for you.',
    body: 'Reviews decide who gets the call. We ask for one after every completed job, by text and email, and answer every review within a day so your profile looks as good as your work.',
    includes: [
      'Text and email review requests after job completion',
      'Google, Yelp, Angi and BBB monitoring',
      'Written replies to every review within 24 hours',
      'Review widgets for your site and proposals',
    ],
  },
  {
    slug: 'lead-follow-up',
    icon: 'messageSquare',
    title: 'Lead follow-up systems',
    short: 'Missed-call text back, instant quote replies and a CRM your crew will actually use.',
    body: 'Half of contractor leads never get a call back. We put a system in place so every call, text and form gets an answer in minutes, and every estimate gets followed up until it is a yes or a no.',
    includes: [
      'Missed-call text back in under 60 seconds',
      'One shared inbox for calls, texts and web forms',
      'Automated estimate follow-ups and appointment reminders',
      'CRM setup and training for the office and the crew',
    ],
  },
  {
    slug: 'brand-content',
    icon: 'camera',
    title: 'Brand and job-site content',
    short: 'Logo, truck wraps, and photo and video from real jobs that build trust before the first call.',
    body: 'Homeowners hire the company that looks like it will still be around in ten years. We give you a brand that holds up on a truck, a yard sign and a phone screen, then keep it fed with real job-site content.',
    includes: [
      'Logo, colors and a brand kit your printer can use',
      'Truck wrap and yard sign design',
      'Monthly job-site photo and video shoots',
      'Social posts written and scheduled for you',
    ],
  },
]

export const TRADES = [
  { name: 'Roofing', icon: 'home' },
  { name: 'HVAC', icon: 'wind' },
  { name: 'Plumbing', icon: 'droplet' },
  { name: 'Electrical', icon: 'zap' },
  { name: 'Remodeling', icon: 'layout' },
  { name: 'Concrete and paving', icon: 'truck' },
  { name: 'Landscaping', icon: 'leaf' },
  { name: 'Painting', icon: 'brush' },
  { name: 'Solar', icon: 'sun' },
  { name: 'General contractors', icon: 'hardHat' },
]

export const PROCESS = [
  {
    step: '01',
    icon: 'search',
    title: 'Audit',
    lead: 'A free 48-hour marketing audit.',
    body: 'We review your site, Google profile, ads and reviews, then show you exactly where jobs are leaking and what they are worth.',
  },
  {
    step: '02',
    icon: 'calendar',
    title: 'Plan',
    lead: 'A 90-day plan at a fixed monthly price.',
    body: 'You get a scope, a budget and a forecast before you sign anything. Month to month. No long-term contracts.',
  },
  {
    step: '03',
    icon: 'rocket',
    title: 'Launch',
    lead: 'Site and campaigns live in 30 days.',
    body: 'We build, you approve, we launch. Call tracking and reporting are on from day one, so nothing is a guess.',
  },
  {
    step: '04',
    icon: 'trendingUp',
    title: 'Scale',
    lead: 'Weekly tuning, monthly strategy call.',
    body: 'We report what you care about: calls, quotes and booked jobs. When a channel works, we put more into it.',
  },
]

export const STATS = [
  { value: '3.4x', label: 'Average return on ad spend' },
  { value: '48 hr', label: 'Audit turnaround' },
  { value: '30 days', label: 'From kickoff to launch' },
  { value: '0', label: 'Long-term contracts' },
]

export const CASE_STUDIES = [
  {
    trade: 'Roofing',
    market: 'Dallas to Fort Worth, TX',
    company: 'Summit Roofing',
    headline: '214% more calls in 90 days',
    challenge: 'Two leads a week from word of mouth, a site that did not load on phones, and no reviews since 2023.',
    did: 'New site with storm-damage landing pages, Local Services Ads plus search campaigns, and review requests after every job.',
    results: [
      { value: '214%', label: 'More calls' },
      { value: '$38', label: 'Cost per lead' },
      { value: '4.9', label: 'Rating, 120+ reviews' },
    ],
  },
  {
    trade: 'HVAC',
    market: 'Phoenix, AZ',
    company: 'Desert Air Heating and Cooling',
    headline: '#1 in the map pack across 14 cities',
    challenge: 'Busy in July, dead in February, and invisible outside the home city.',
    did: 'City pages for every suburb, Google Business Profile rebuild, and a maintenance-plan campaign to smooth the off-season.',
    results: [
      { value: '#1', label: 'Map pack, 14 cities' },
      { value: '3.1x', label: 'Return on ad spend' },
      { value: '61', label: 'Maintenance plans sold' },
    ],
  },
  {
    trade: 'Remodeling',
    market: 'Charlotte, NC',
    company: 'Carolina Kitchen and Bath',
    headline: 'Quote-to-close from 22% to 41%',
    challenge: 'High-ticket leads waiting days for a reply, and estimates that went out and were never followed up.',
    did: 'Portfolio site with financing calls to action, a shared inbox with missed-call text back, and automated estimate follow-ups.',
    results: [
      { value: '41%', label: 'Quote-to-close' },
      { value: '8 min', label: 'Average first reply' },
      { value: '$180k', label: 'First-month signed work' },
    ],
  },
]

export const TESTIMONIALS = [
  {
    quote: 'We went from two calls a week to two a day. The missed-call text back alone paid for the first year.',
    name: 'Mike Alvarez',
    role: 'Owner, Alvarez Roofing',
  },
  {
    quote: 'First page of Google in three months and the phone has not stopped. They report in plain English, not marketing talk.',
    name: 'Dana Whitfield',
    role: 'Owner, Whitfield Heating and Air',
  },
  {
    quote: 'They built the site, ran the ads and set up the follow-ups. I just show up and quote the job.',
    name: 'Chris Okafor',
    role: 'Owner, Okafor Electric',
  },
]

export const PLANS = [
  {
    name: 'Starter',
    price: '$1,500',
    period: 'per month',
    blurb: 'Get found and look the part.',
    features: [
      'Contractor website, hosted and maintained',
      'Google Business Profile optimization',
      'Review request automation',
      'Monthly report and check-in',
    ],
    cta: 'Start with Starter',
  },
  {
    name: 'Growth',
    price: '$3,000',
    period: 'per month',
    blurb: 'Fill the calendar in every city you serve.',
    featured: true,
    features: [
      'Everything in Starter',
      'Local SEO with city and service pages',
      'Google Ads and Local Services Ads management',
      'Missed-call text back and shared inbox',
      'Weekly tuning, monthly strategy call',
    ],
    cta: 'Grow with Growth',
    note: 'Ad spend billed separately by Google.',
  },
  {
    name: 'Boss',
    price: '$5,500',
    period: 'per month',
    blurb: 'The whole marketing department, on call.',
    features: [
      'Everything in Growth',
      'Monthly job-site photo and video shoots',
      'Brand kit, truck wraps and yard signs',
      'CRM setup and team training',
      'Dedicated strategist and same-day support',
    ],
    cta: 'Talk to us about Boss',
  },
]

export const FAQS = [
  {
    q: 'Do you require long-term contracts?',
    a: 'No. Every plan is month to month after the first 90 days, and the first 90 days come with a written forecast so you know what to expect. If we are not producing, you should be able to leave.',
  },
  {
    q: 'Which trades do you work with?',
    a: 'Roofing, HVAC, plumbing, electrical, remodeling, concrete and paving, landscaping, painting, solar and general contractors. We do not take on two direct competitors in the same market.',
  },
  {
    q: 'How fast will I see results?',
    a: 'Ads and Local Services Ads produce calls within the first two weeks of launch. Local SEO builds over 60 to 120 days. Review systems start working the day they turn on.',
  },
  {
    q: 'Do I own my website and ad accounts?',
    a: 'Yes. The domain, the site, the Google Business Profile and the ad accounts are all in your name. If you leave, you keep everything.',
  },
  {
    q: 'What does it cost?',
    a: 'Plans start at $1,500 a month. Most growing companies land on Growth at $3,000 a month plus their ad budget. The free audit comes with a written recommendation and a price before you commit to anything.',
  },
]

export const VALUES = [
  {
    icon: 'dollar',
    title: 'Booked jobs are the metric',
    body: 'Not impressions, not rankings for their own sake. We report calls, quotes and signed work, and we are judged on it.',
  },
  {
    icon: 'messageSquare',
    title: 'Straight talk',
    body: 'Plain-English reports and a real person who answers the phone. If something is not working, you hear it from us first.',
  },
  {
    icon: 'shield',
    title: 'You own everything',
    body: 'Your domain, site, profiles and ad accounts stay in your name. No hostage situations, ever.',
  },
  {
    icon: 'hardHat',
    title: 'Built for the trades',
    body: 'We only work with contractors, so we already know your season, your service area and what a good lead sounds like.',
  },
]

export const TEAM = [
  { name: 'Jordan Lee', role: 'Founder, strategy', initials: 'JL' },
  { name: 'Sam Patel', role: 'Paid media', initials: 'SP' },
  { name: 'Riley Chen', role: 'Web and local SEO', initials: 'RC' },
]

export const TRADE_OPTIONS = TRADES.map((t) => t.name).concat(['Other'])
